import React, { useCallback, useContext, useEffect, useRef } from 'react'
import {
  NativeSyntheticEvent,
  ScrollView,
  TextInputKeyPressEventData,
  TouchableOpacity,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useDispatch, useSelector } from 'react-redux'
import { ApolloError } from '@apollo/client'
import { useNavigation } from '@react-navigation/native'
import { NavigationContainer } from '@screens/Common/containers'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { Button, Icon, Input, Text } from '@components/base'
import { NavigatorContext, useSnack } from '@utils'
import { userSelector } from '@selectors/app.ts'
import {
  calculateLeftTime,
  maskPhoneNumber,
  useCodeStates,
  useTimer,
} from '@screens/MfaChallenge/utils.ts'
import { VerifyMfaSmsCodeActionPayload } from '@src/types.ts'
import Storage from '@utils/storage.ts'
import { useLazyScreenPreload } from '@src/navigation/utils'

const TIMER_DURATION_SECONDS = 60 * 5
const CODES_LENGTH = 6

export const MfaChallengeScreen = () => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const user = useSelector(userSelector)
  const dispatch = useDispatch()
  const showSnack = useSnack()
  const { secondsLeft, startCounter } = useTimer(TIMER_DURATION_SECONDS)
  const { allCodeRefs, codes, setCodes } = useCodeStates(CODES_LENGTH)
  const navigation = useNavigation()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const verifyingRef = useRef(false)

  useLazyScreenPreload('SupportHub')

  const buttonDisabled = codes.some((code) => !code)

  const continueOnPress = () => {
    const mfaSmsCode = codes.join('')

    if (secondsLeft < 1) {
      showSnack('Code has expired', null, 'error')
      return
    }

    if (verifyingRef.current) {
      return
    }

    verifyingRef.current = true
    enableLoader()

    dispatch({
      type: 'mfaChallenge/verifyMfaSmsCode',
      payload: { mfaSmsCode },
      success: ({ mfaToken }: VerifyMfaSmsCodeActionPayload) => {
        dispatch({
          type: 'app/updateAppState',
          payload: {
            isMfaRequired: false,
          },
        })
        Storage.set(Storage.MFA_TOKEN, mfaToken)
      },
      failure: (error) => {
        const errorMessage = error?.message

        if (errorMessage === 'invalid_mfa_sms_code') {
          showSnack('Invalid code submitted', null, 'error')
        } else {
          showSnack('Could not verify code', null, 'error')
        }
      },
      complete: () => {
        disableLoader()
        verifyingRef.current = false
      },
    })
  }

  const contactSupport = () => {
    navigation.navigate('SupportHub')
  }

  const onSignOutPress = () => {
    dispatch({ type: 'app/logout' })
  }

  const dispatchSendMfaSmsCode = useCallback(() => {
    dispatch({
      type: 'mfaChallenge/sendMfaSmsCode',
      success: () => startCounter(),
      failure: (error: any) => {
        if (
          error instanceof ApolloError &&
          error.networkError &&
          'statusCode' in error.networkError &&
          error.networkError.statusCode === 429
        ) {
          showSnack(
            'You have reached the limit for sending codes. Please try again later.',
            null,
            'error',
          )
        } else {
          showSnack('Could not send code', null, 'error')
        }
      },
    })
  }, [dispatch, showSnack, startCounter])

  const onResendPress = () => {
    dispatchSendMfaSmsCode()
    setCodes(Array(CODES_LENGTH).fill(''))
  }

  const needHelpLink = (
    <TouchableOpacity
      onPress={contactSupport}
      accessibilityLabel="Resend"
      style={{ marginRight: 24 }}
    >
      <Text type="regular" style={styles.infoText}>
        Need Help?
      </Text>
    </TouchableOpacity>
  )

  const signOutLink = (
    <TouchableOpacity
      onPress={onSignOutPress}
      accessibilityLabel="Sign Out"
      style={{ marginLeft: 24 }}
    >
      <Text type="regular" style={styles.infoText}>
        Sign out
      </Text>
    </TouchableOpacity>
  )

  useEffect(() => {
    dispatchSendMfaSmsCode()
  }, [dispatchSendMfaSmsCode])

  useEffect(
    () => () => {
      if (verifyingRef.current) {
        disableLoader()
        verifyingRef.current = false
      }
    },
    [disableLoader],
  )

  const onKeyPressHandler = (
    event: NativeSyntheticEvent<TextInputKeyPressEventData>,
    ix: number,
  ) => {
    if (event.nativeEvent.key === 'Backspace') {
      const newCodeValues = codes.slice()
      newCodeValues[ix] = ''
      setCodes(newCodeValues)

      if (ix > 0) {
        allCodeRefs[ix - 1].current?.focus()
      }
    }
  }

  const onChangeTextHandler = (value: string, ix: number) => {
    const values = value.split('')

    // detect onPaste event
    if (values.length === CODES_LENGTH) {
      setCodes(values)
      allCodeRefs[ix].current?.blur()
      return
    }

    const newCodeValues = codes.slice()
    newCodeValues[ix] = values[values.length - 1]
    setCodes(newCodeValues)

    if (!value) {
      return
    }

    if (ix === CODES_LENGTH - 1) {
      allCodeRefs[ix].current?.blur()
      return
    }

    allCodeRefs[ix + 1].current?.focus()
  }

  return (
    <NavigationContainer
      title="Nutrisense"
      rightAccessories={[needHelpLink]}
      navigationBarProps={{ leftAccessory: signOutLink }}
    >
      <ScrollView
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
        bounces={false}
      >
        <View style={styles.headerContainer}>
          <Text type="title-3" style={styles.text}>
            Two Factor Authentication
          </Text>

          <Text type="regular" style={styles.text}>
            Please enter the six digit code we sent to your phone number{' '}
            {maskPhoneNumber(user?.phoneNumber || '')}
          </Text>

          <Text type="regular" style={styles.text}>
            {calculateLeftTime(secondsLeft)}
          </Text>

          <View style={styles.codeContainer}>
            {Array.from(Array(allCodeRefs.length)).map((_, ix) => {
              return (
                <Input
                  textContentType="oneTimeCode" // iOS autocomplete otp
                  autoComplete="one-time-code" // android autocomplete otp
                  caretHidden
                  autoFocus={ix === 0}
                  ref={allCodeRefs[ix]}
                  value={codes[ix]}
                  key={`input-${ix}`}
                  textAlign="center"
                  style={styles.codeInput}
                  keyboardType="numeric"
                  onKeyPress={(event) => onKeyPressHandler(event, ix)}
                  onChangeText={(value) => onChangeTextHandler(value, ix)}
                />
              )
            })}
          </View>

          <View style={styles.resendContainer}>
            <Text type="regular" style={styles.text}>
              I didn't receive any code.{' '}
            </Text>

            <TouchableOpacity onPress={onResendPress} accessibilityLabel="Resend">
              <Text type="regular" style={styles.infoText}>
                Resend
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={{ gap: 24 }}>
          <Button
            type="primary"
            disabled={buttonDisabled}
            size="block"
            onPress={continueOnPress}
            accessibilityLabel="Continue"
          >
            Continue
          </Button>

          <View style={styles.detailsContainer}>
            <Icon name="lock" style={styles.lockIcon} />
            <Text type="small">All the details you share are secure and confidential</Text>
          </View>
        </View>
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  content: {
    flexGrow: 1,
    paddingTop: 16,
    marginHorizontal: 16,
    alignContent: 'center',
  },
  text: {
    textAlign: 'center',
  },
  codeContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  codeInput: {
    flex: 1,
    color: 'theme.solid.white',
    borderRadius: 50,
    borderColor: 'theme.surface.base1',
  },
  headerContainer: {
    flex: 1,
    gap: 24,
    paddingHorizontal: 8,
  },
  infoText: {
    color: 'theme.info.base',
  },
  lockIcon: {
    color: 'theme.success.lighter',
    height: 16,
    width: 16,
  },
  resendContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 32,
  },
  detailsContainer: {
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
})
